import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type SetStateAction } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, InputNumber, type InputRef } from "antd";
import { FloppyDisk, Plus, Trash } from "@phosphor-icons/react";
import useMobileGrid from "../../shared/grid/useMobileGrid.ts";
import { t, getLocale, useStoreLanguage, localizeDigits, parseLocalizedNumber } from "../../../features/store-language/i18n.ts";
import { agencySessionKey, canWriteAgencyCommerce, canChangeOrderStatus, isCompanySalesAgent, subscribeAgencySession } from "../../../utils/agencySession.ts";
import AppFeedback from "../../shared/AppFeedback.tsx";
import AuthenticatedImage from "../../shared/AuthenticatedImage.tsx";
import AccountItemPickerDialog from "../../shared/account/AccountItemPickerDialog.tsx";
import AccountOrderGrid, { AccountOrderMenu, AccountOrderColumn } from "./AccountOrderGrid.tsx";
import OrderAttachments, { type AttachmentTask } from "./OrderAttachments.tsx";
import AccountOrderNotes from "./AccountOrderNotes.tsx";
import OrderAgencyField from "../shared/OrderAgencyField.tsx";
import StoreTextField from "../shared/StoreTextField.tsx";
import AccountDocumentHeader from "../../shared/account/AccountDocumentHeader.tsx";
import PortalDialog from "../../shared/PortalDialog.tsx";
import { AccountButton, AccountSelect, useAccountAction } from "../../shared/account/AccountUI.tsx";
import { PendingRing } from "../../shared/LoadingRing.jsx";
import DirectionalIcon from "../../../features/navigation/DirectionalIcon.tsx";
import { clearOrderRecovery, orderDraftSignature, orderRecoveryKey, readOrderRecovery, retainOrderRecovery } from "./accountOrderRecovery.ts";
import { compactOrderNumber } from "./orderNumber.ts";
import {
  AccountOrder, AccountOrderAttachment, AccountOrderDraft, AccountOrderLine, accountOrderStatusLabel,
  createAccountOrder, deleteAccountOrder,
  changeAccountOrderStatus, editAccountOrder, findAccountOrderProducts, pendingAccountOrder, readAccountOrder,
  attachAccountOrderFile, discardAccountOrderAttachmentFile, removeAccountOrderAttachment,
  uploadAccountOrderAttachmentFile,
} from "./accountOrderService.ts";

function orderItemsSignature(items: readonly AccountOrderLine[]) {
  return JSON.stringify(items.map(function (line) {
    return [line.productVariantId, line.quantity];
  }).sort(function (left, right) {
    return left[0] - right[0];
  }));
}

export default function AccountOrderEditor() {
  const { id } = useParams();
  const sessionKey = useSyncExternalStore(subscribeAgencySession, agencySessionKey);
  // Separate component instances keep drafts and concurrency versions scoped to the route.
  return (
    <OrderEditor
      key={`${sessionKey}:${id || "create"}`}
      orderId={id}
    />
  );
}

function OrderEditor({ orderId }: { orderId?: string }) {
  const language = useStoreLanguage();
  const mobile = useMobileGrid();
  const navigate = useNavigate();
  const isNew = orderId === undefined;
  const validId = isNew || /^[1-9]\d*$/.test(orderId || "");
  const [order, setOrder] = useState<AccountOrder>();
  const [draft, setDraft] = useState<AccountOrderDraft>(function () {
    return (isNew ? pendingAccountOrder()?.draft : undefined) || { description: "", items: [] };
  });
  const [uncertain, setUncertain] = useState(function () { return isNew && Boolean(pendingAccountOrder()); });
  const [loading, setLoading] = useState(!isNew);
  const [pending, setPending] = useState<"save" | "delete" | "status">();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [revision, setRevision] = useState(0);
  const [saveRevision, setSaveRevision] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [attachmentTask, setAttachmentTask] = useState<AttachmentTask>();
  const recoveryKey = useMemo(function () { return orderRecoveryKey(orderId || "create"); }, [orderId]);
  const saveAction = useAccountAction(`save:${recoveryKey}`);
  const recovered = useRef(false);
  const mounted = useRef(true);
  const orderRef = useRef(order);
  orderRef.current = order;
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const dirtyRef = useRef(false);
  const locked = useRef(false);
  const automaticSaveInFlight = useRef(false);
  const titleInputRef = useRef<InputRef>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState<AccountOrderLine[]>([]);
  const [selected, setSelected] = useState<AccountOrderLine>();
  const [quantity, setQuantity] = useState<number | null>(1);
  const [searching, setSearching] = useState(false);
  const [searchRevision, setSearchRevision] = useState(0);
  const [searchFailed, setSearchFailed] = useState(false);
  const productOptions = useMemo(function () {
    return products.map(function (product) {
      return { value: product.productVariantId, label: product.title };
    });
  }, [products]);
  const canWrite = canWriteAgencyCommerce();
  const editable = canWrite && (isNew || order?.status === "Draft" || order?.status === "Confirmed");
  const disabled = !editable || loading || Boolean(pending) || Boolean(attachmentTask) || uncertain;
  const deleteDisabled = !canWrite || order?.status !== "Draft" || loading || Boolean(pending) || saveAction.pending || Boolean(attachmentTask) || uncertain;

  useEffect(function () {
    if (isNew || !validId) { setLoading(false); return; }
    let active = true;
    setLoading(true);
    readAccountOrder(Number(orderId)).then(function (result) {
      if (!active) return;
      if (!["Draft", "Confirmed"].includes(result.status)) {
        // A newly received/approved/cancelled order must never restore an editable stale draft.
        recovered.current = true;
        dirtyRef.current = false;
        setDirty(false);
        setSaveFailed(false);
        setOrder(result);
        setDraft({ salesAgencyId: result.salesAgencyId, title: result.title || "", description: result.description || "", items: result.items });
        return;
      }
      if (!recovered.current) {
        recovered.current = true;
        const recovery = readOrderRecovery(recoveryKey);
        const saved = recovery ? { ...recovery, draft: { ...recovery.draft, salesAgencyId: recovery.draft.salesAgencyId ?? result.salesAgencyId } } : undefined;
        if (saved && (result.status === "Draft" || result.status === "Confirmed") && orderDraftSignature(saved.draft) !== orderDraftSignature({ ...result, description: result.description || "" })) {
          setOrder({ ...result, version: saved.version });
          setDraft(saved.draft);
          dirtyRef.current = true;
          setDirty(true);
          setSaveFailed(true);
          return;
        }
        clearOrderRecovery(recoveryKey);
      }
      if (dirtyRef.current) {
        // A language refresh must not replace the revision or discard local edits.
        setDraft(function (current) {
          return { ...current, items: current.items.map(function (line) {
            const translated = result.items.find(function (item) { return item.id === line.id; });
            return translated ? { ...line, title: translated.title, code: translated.code } : line;
          }) };
        });
      } else {
        setOrder(result);
        setDraft({ salesAgencyId: result.salesAgencyId, title: result.title || "", description: result.description || "", items: result.items });
      }
    }).catch(function (error) {
      if (active) AppFeedback.httpError(error);
    }).finally(function () { if (active) setLoading(false); });
    return function () { active = false; };
  }, [isNew, validId, orderId, language, revision, recoveryKey]);

  useEffect(function () {
    mounted.current = true;
    return function () { mounted.current = false; };
  }, []);

  useEffect(function () {
    if (!addOpen) return;
    let active = true;
    const controller = new AbortController();
    setProducts([]);
    setSearching(true);
    setSearchFailed(false);
    const timer = setTimeout(function () {
      findAccountOrderProducts(search.trim(), controller.signal).then(function (result) {
        if (active) setProducts(result);
      }).catch(function (error) {
        if (active && !controller.signal.aborted) { setProducts([]); setSearchFailed(true); AppFeedback.httpError(error); }
      }).finally(function () { if (active) setSearching(false); });
    }, search ? 250 : 0);
    return function () { active = false; controller.abort(); clearTimeout(timer); };
  }, [addOpen, search, language, searchRevision]);

  useEffect(function () {
    function beforeUnload(event: BeforeUnloadEvent) {
      if (!dirtyRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    }
    window.addEventListener("beforeunload", beforeUnload);
    return function () { window.removeEventListener("beforeunload", beforeUnload); };
  }, []);

  const change = useCallback(function (next: SetStateAction<AccountOrderDraft>) {
    if (disabled || (locked.current && !automaticSaveInFlight.current)) return false;
    const value = typeof next === "function" ? next(draftRef.current) : next;
    if (JSON.stringify(value) === JSON.stringify(draftRef.current)) return true;
    const currentOrder = orderRef.current;
    if (!isNew && currentOrder) {
      try { retainOrderRecovery(recoveryKey, currentOrder.version, value); }
      catch {
        AppFeedback.error({ title: t("نگهداری تغییرات در این مرورگر ممکن نیست؛ دوباره تلاش کنید.") });
        return false;
      }
    }
    draftRef.current = value;
    dirtyRef.current = true;
    setDirty(true);
    setDraft(value);
    return true;
  }, [disabled, isNew, recoveryKey]);

  async function refresh() {
    if (loading || pending || locked.current) return;
    locked.current = true;
    setLoading(true);
    try {
      if (dirtyRef.current) {
        const result = await AppFeedback.confirm({
          title: t("تغییرات ذخیره‌نشده"), text: t("تغییرات ذخیره‌نشده کنار گذاشته شوند؟"),
          confirmButtonText: t("ادامه"), cancelButtonText: t("انصراف"), showCancelButton: true,
        });
        if (!result.isConfirmed || !mounted.current) return;
      }
      dirtyRef.current = false;
      setDirty(false);
      setSaveFailed(false);
      clearOrderRecovery(recoveryKey);
      setRevision(function (value) { return value + 1; });
    } finally {
      locked.current = false;
      if (mounted.current) setLoading(false);
    }
  }

  function save(automatic = false) {
    return saveAction.run(function () { return persist(automatic); }, { background: automatic });
  }

  async function persist(automatic: boolean) {
    if (!editable || loading || locked.current || (!isNew && (!order || !dirty))) return;
    const snapshot = draftRef.current;
    const baseOrder = orderRef.current;
    if (!isNew && baseOrder && !saveFailed && orderDraftSignature(snapshot) === orderDraftSignature({ ...baseOrder, description: baseOrder.description || "" })) {
      clearOrderRecovery(recoveryKey);
      dirtyRef.current = false;
      setDirty(false);
      return;
    }
    if (snapshot.items.length > 500 || snapshot.items.some(function (line) {
      return !Number.isInteger(line.quantity) || line.quantity < 1 || line.quantity > 100000;
    })) {
      if (!automatic) AppFeedback.error({ title: t("سفارش حداکثر ۵۰۰ قلم با تعداد صحیح و معتبر می‌تواند داشته باشد.") });
      return;
    }
    if (!snapshot.title?.trim()) {
      if (!automatic) AppFeedback.error({ title: t("عنوان سفارش را وارد کنید.") });
      return;
    }
    locked.current = true;
    automaticSaveInFlight.current = automatic;
    if (!automatic) setPending("save");
    try {
      if (isNew) {
        const result = await createAccountOrder(snapshot);
        if (!mounted.current) return;
        dirtyRef.current = false;
        navigate(`/account/orders/${result.orderId}`, { replace: true });
      } else if (baseOrder) {
        let result: AccountOrder;
        if (saveFailed) {
          // Only recovery needs a read: an interrupted response may already have committed.
          const current = await readAccountOrder(baseOrder.id);
          if (!mounted.current) return;
          if (orderDraftSignature({ ...current, description: current.description || "" }) === orderDraftSignature(snapshot)) {
            result = current;
          } else {
            if (current.version !== baseOrder.version) {
              AppFeedback.error({ title: t("سفارش تغییر کرده است؛ تغییرات ذخیره‌نشده را بررسی کنید و سپس صفحه را تازه‌سازی کنید.") });
              return;
            }
            result = await editAccountOrder(baseOrder, snapshot);
          }
        } else {
          result = await editAccountOrder(baseOrder, snapshot);
        }
        const retained = readOrderRecovery(recoveryKey);
        if (retained?.version === baseOrder.version && orderDraftSignature(retained.draft) === orderDraftSignature(snapshot)) {
          clearOrderRecovery(recoveryKey);
        }
        if (!mounted.current) return;
        const focusedTitle = automatic && titleInputRef.current?.input === document.activeElement ? {
          start: titleInputRef.current.input.selectionStart,
          end: titleInputRef.current.input.selectionEnd,
          direction: titleInputRef.current.input.selectionDirection,
        } : undefined;
        const latestDraft = draftRef.current;
        const hasNewerEdits = orderDraftSignature(latestDraft) !== orderDraftSignature(snapshot);
        const persistedItems = new Map(result.items.map(function (line) { return [line.productVariantId, line]; }));
        const submittedItemChanges = orderItemsSignature(snapshot.items) !== orderItemsSignature(baseOrder.items);
        const reconcileItems = submittedItemChanges && (result.items.length !== snapshot.items.length || snapshot.items.some(function (line) {
          const persisted = persistedItems.get(line.productVariantId);
          return !persisted || persisted.id !== line.id || persisted.fileId !== line.fileId
            || persisted.title !== line.title || persisted.code !== line.code
            || persisted.sourceUnitPrice !== line.sourceUnitPrice || persisted.area !== line.area
            || persisted.reedCountTitle !== line.reedCountTitle || persisted.paletteTitle !== line.paletteTitle;
        }));
        const latestPersistedDraft = reconcileItems ? {
          ...latestDraft,
          items: latestDraft.items.map(function (line) {
            const persisted = persistedItems.get(line.productVariantId);
            const existedInSnapshot = snapshot.items.some(function (savedLine) {
              return savedLine.productVariantId === line.productVariantId;
            });
            return persisted && existedInSnapshot ? { ...persisted, quantity: line.quantity } : line;
          }),
        } : latestDraft;
        orderRef.current = result;
        setSaveFailed(false);
        setOrder(result);
        dirtyRef.current = hasNewerEdits;
        setDirty(hasNewerEdits);
        if (hasNewerEdits) {
          draftRef.current = latestPersistedDraft;
          if (reconcileItems) setDraft(latestPersistedDraft);
          retainOrderRecovery(recoveryKey, result.version, latestPersistedDraft);
        } else if (automatic && reconcileItems) {
          draftRef.current = latestPersistedDraft;
          setDraft(latestPersistedDraft);
        } else if (!automatic) {
          const persistedDraft = { salesAgencyId: result.salesAgencyId, title: result.title || "", description: result.description || "", items: result.items };
          draftRef.current = persistedDraft;
          setDraft(persistedDraft);
        }
        if (focusedTitle) {
          window.requestAnimationFrame(function () {
            const input = titleInputRef.current?.input;
            if (!input?.isConnected) return;
            if (document.activeElement !== document.body && document.activeElement !== input) return;
            input.focus({ preventScroll: true });
            if (focusedTitle.start !== null && focusedTitle.end !== null) {
              input.setSelectionRange(focusedTitle.start, focusedTitle.end, focusedTitle.direction || undefined);
            }
          });
        }
      }
      if (!automatic) AppFeedback.success({ title: t("سفارش ذخیره شد.") });
    } catch (error) {
      if (!mounted.current) return;
      setSaveFailed(true);
      setUncertain(isNew && Boolean(pendingAccountOrder()));
      AppFeedback.httpError(error);
    } finally {
      automaticSaveInFlight.current = false;
      locked.current = false;
      if (mounted.current) {
        if (automatic) setSaveRevision(function (value) { return value + 1; });
        else setPending(undefined);
      }
    }
  }

  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(function () {
    if (isNew || !dirty || saveFailed || disabled) return;
    if (draft.items.length > 500 || draft.items.some(function (line) {
      return !Number.isInteger(line.quantity) || line.quantity < 1 || line.quantity > 100000;
    })) return;
    const timer = window.setTimeout(function () { void saveRef.current(true); }, 500);
    return function () { window.clearTimeout(timer); };
  }, [isNew, dirty, saveFailed, disabled, draft, saveRevision]);

  async function remove() {
    if (!order || deleteDisabled || locked.current) return;
    locked.current = true;
    setPending("delete");
    try {
      const result = await AppFeedback.confirmDestructive({
        title: t("حذف سفارش"), text: t("سفارش شماره {{value0}} حذف شود؟", { value0: order.id }),
        confirmButtonText: t("بله، حذف شود"), cancelButtonText: t("انصراف"), showCancelButton: true,
      });
      if (!result.isConfirmed) return;
      setPending("delete");
      await deleteAccountOrder(order);
      clearOrderRecovery(recoveryKey);
      dirtyRef.current = false;
      navigate("/account/orders", { replace: true });
    } catch (error) { AppFeedback.httpError(error); }
    finally { locked.current = false; setPending(undefined); }
  }

  async function changeStatus(status: AccountOrder["status"], reason?: string) {
    if (!order || !canChangeOrderStatus() || loading || locked.current || order.status === status) return;
    if (dirtyRef.current) {
      AppFeedback.error({ title: t("ابتدا تغییرات سفارش را ذخیره کنید.") });
      return;
    }
    if (status === "Cancelled" && !reason?.trim()) {
      AppFeedback.error({ title: t("دلیل لغو سفارش را وارد کنید.") });
      return;
    }
    locked.current = true;
    setPending("status");
    try {
      await changeAccountOrderStatus(order, status, reason);
      setCancelOpen(false);
      setCancellationReason("");
      // Reload the committed status and rowversion before allowing another write.
      setOrder(undefined);
      setRevision(function (value) { return value + 1; });
      AppFeedback.success({ title: t("وضعیت سفارش تغییر کرد.") });
    } catch (error) {
      AppFeedback.httpError(error);
    } finally { locked.current = false; setPending(undefined); }
  }

  function validAttachmentDraft() {
    if (!draft.title?.trim()) {
      AppFeedback.error({ title: t("برای افزودن پیوست، ابتدا عنوان سفارش را وارد کنید.") });
      return false;
    }
    if (draft.items.length > 500 || draft.items.some(function (line) {
      return !Number.isInteger(line.quantity) || line.quantity < 1 || line.quantity > 100000;
    })) {
      AppFeedback.error({ title: t("سفارش حداکثر ۵۰۰ قلم با تعداد صحیح و معتبر می‌تواند داشته باشد.") });
      return false;
    }
    return true;
  }

  async function uploadAttachment(file: File) {
    if (!editable || loading || locked.current || attachmentTask || !validAttachmentDraft()) return;
    if (!isNew && (!order || saveFailed)) {
      AppFeedback.error({ title: t("ابتدا تغییرات سفارش را با موفقیت ذخیره کنید.") });
      return;
    }
    locked.current = true;
    let persistedOrderId = order?.id;
    let createdOrderId: number | undefined;
    let uploadedFileId: number | undefined;
    let persistenceCompleted = !isNew && !dirty;
    try {
      if (isNew) {
        setAttachmentTask({ stage: "saving", fileName: file.name });
        const created = await createAccountOrder(draft);
        persistedOrderId = created.orderId;
        createdOrderId = created.orderId;
        persistenceCompleted = true;
        dirtyRef.current = false;
        setDirty(false);
      } else if (order && dirty) {
        setAttachmentTask({ stage: "saving", fileName: file.name });
        const saved = await editAccountOrder(order, draft);
        persistedOrderId = saved.id;
        persistenceCompleted = true;
        clearOrderRecovery(recoveryKey);
        dirtyRef.current = false;
        setDirty(false);
        setSaveFailed(false);
        setOrder(saved);
        setDraft({ salesAgencyId: saved.salesAgencyId, title: saved.title || "", description: saved.description || "", items: saved.items });
      }
      if (!persistedOrderId) return;
      setAttachmentTask({ stage: "uploading", fileName: file.name });
      const uploaded = await uploadAccountOrderAttachmentFile(persistedOrderId, file);
      uploadedFileId = uploaded.fileId;
      setAttachmentTask({ stage: "attaching", fileName: uploaded.fileName });
      try {
        await attachAccountOrderFile(persistedOrderId, uploaded.fileId);
      } catch (error) {
        try { await discardAccountOrderAttachmentFile(persistedOrderId, uploaded.fileId); } catch { /* Preserve the original attachment error. */ }
        uploadedFileId = undefined;
        throw error;
      }
      uploadedFileId = undefined;
      AppFeedback.success({ title: t("فایل به سفارش پیوست شد.") });
      if (createdOrderId) {
        navigate(`/account/orders/${createdOrderId}`, { replace: true });
        return;
      }
      const refreshed = await readAccountOrder(persistedOrderId);
      if (!mounted.current) return;
      setOrder(refreshed);
      setDraft({ salesAgencyId: refreshed.salesAgencyId, title: refreshed.title || "", description: refreshed.description || "", items: refreshed.items });
    } catch (error) {
      if (!persistenceCompleted) {
        setSaveFailed(true);
        setUncertain(isNew && Boolean(pendingAccountOrder()));
      }
      if (uploadedFileId) {
        try { await discardAccountOrderAttachmentFile(persistedOrderId!, uploadedFileId); } catch { /* Best-effort orphan cleanup. */ }
      }
      AppFeedback.httpError(error);
      if (createdOrderId) navigate(`/account/orders/${createdOrderId}`, { replace: true });
    } finally {
      locked.current = false;
      if (mounted.current) setAttachmentTask(undefined);
    }
  }

  async function removeAttachment(attachment: AccountOrderAttachment) {
    if (!order || !editable || loading || locked.current || attachmentTask) return;
    locked.current = true;
    setAttachmentTask({ stage: "deleting", fileName: attachment.fileName, attachmentId: attachment.id });
    try {
      await removeAccountOrderAttachment(order.id, attachment.id);
      const refreshed = await readAccountOrder(order.id);
      if (!mounted.current) return;
      setOrder(refreshed);
      if (!dirtyRef.current) {
        setDraft({ salesAgencyId: refreshed.salesAgencyId, title: refreshed.title || "", description: refreshed.description || "", items: refreshed.items });
      }
      AppFeedback.success({ title: t("پیوست از سفارش حذف شد.") });
    } catch (error) {
      AppFeedback.httpError(error);
    } finally {
      locked.current = false;
      if (mounted.current) setAttachmentTask(undefined);
    }
  }

  function addLine() {
    if (disabled || !selected || searching || searchFailed) return;
    if (!quantity || !Number.isInteger(quantity) || quantity < 1 || quantity > 100000) {
      AppFeedback.error({ title: t("تعداد باید عدد صحیح بین ۱ و ۱۰۰۰۰۰ باشد.") });
      return;
    }
    if (draft.items.some(function (line) { return line.productVariantId === selected.productVariantId; })) {
      AppFeedback.error({ title: t("این کالا در سفارش وجود دارد؛ تعداد همان ردیف را تغییر دهید.") });
      return;
    }
    change({ ...draft, items: [...draft.items, { ...selected, quantity }] });
    setAddOpen(false);
  }

  function measurement(value: number | null) {
    return value == null ? "—" : value.toLocaleString(getLocale(), { maximumFractionDigits: 4 });
  }

  const columns = useMemo<AccountOrderColumn<AccountOrderLine>[]>(function () {
    void language;
    return [
    { colId: "no", headerName: t("ردیف"), valueGetter: "node.rowIndex + 1", sortable: false, filter: false, enableRowGroup: false },
    { colId: "image", headerName: t("تصویر"), sortable: false, enableRowGroup: true,
      cellRenderer: function (params) {
        return params.data ? (
          <AuthenticatedImage
            fileId={params.data.fileId}
            alt={params.data.title}
            style={{ width: 38, height: 38, objectFit: "contain" }}
          />
        ) : null;
      } },
    { field: "code", headerName: t("کد نقشه"), sortable: true, floatingFilter: true, enableRowGroup: true },
    { field: "title", headerName: t("نقشه"), mobileEssential: true, sortable: true, floatingFilter: true, enableRowGroup: true,
      flex: mobile ? 2 : 1,
      cellRenderer: mobile ? function (params) {
        return <span className="account-order-grid__cell-title">{localizeDigits(params.value || "")}</span>;
      } : undefined },
    { field: "quantity", headerName: t("تعداد"), mobileEssential: true, minWidth: mobile ? 80 : 90, sortable: true, floatingFilter: true, enableValue: true,
      cellRenderer: function (params) {
        const line: AccountOrderLine | undefined = params.data;
        if (!line) return params.value;
        return (
          <InputNumber<number>
            className="account-order-quantity"
            inputMode="numeric"
            controls
            aria-label={t("تعداد {{name}}", { name: line.title })}
            min={1}
            max={100000}
            precision={0}
            disabled={disabled}
            value={line.quantity}
            formatter={localizeDigits}
            parser={parseLocalizedNumber}
            onChange={function (value) {
              change(function (current) {
                return { ...current, items: current.items.map(function (item) {
                  return item.id === line.id ? { ...item, quantity: value ?? 0 } : item;
                }) };
              });
            }}
          />
        );
      } },
    { field: "area", headerName: t("متر مربع"), mobileEssential: true, minWidth: mobile ? 64 : 110, editable: false, sortable: true,
      floatingFilter: true, enableValue: true, valueFormatter: function (params) { return measurement(params.value); } },
    { field: "reedCountTitle", headerName: t("شانه"), editable: false, sortable: true, floatingFilter: true, enableRowGroup: true,
      valueFormatter: function (params) { return params.value ? localizeDigits(params.value) : "—"; } },
    { field: "paletteTitle", headerName: t("پالت"), editable: false, sortable: true, floatingFilter: true, enableRowGroup: true,
      valueFormatter: function (params) { return params.value || "—"; } },
    { field: "operation" as keyof AccountOrderLine, headerName: t("عملیات"), mobileEssential: true, hide: mobile && !editable, pinned: "left", sortable: false, filter: false, floatingFilter: false, suppressSharedOperationMenu: true,
      cellRenderer: function (params) {
        const line: AccountOrderLine | undefined = params.data;
        if (!line || !editable) return null;
        return (
          <AccountOrderMenu
            disabled={disabled}
            items={[{
              key: "remove", icon: <Trash size={17} />, label: t("حذف"), danger: true, disabled,
              onClick: function () {
                change(function (current) {
                  return { ...current, items: current.items.filter(function (item) { return item.id !== line.id; }) };
                });
              },
            }]}
          />
        );
      } },
    ];
  }, [change, disabled, editable, language, mobile]);

  return (
    <section className="account-orders-page">
      <AccountDocumentHeader
        className="account-order-header"
        metadata={isNew ? undefined : [
          {
            key: "status",
            label: t("وضعیت"),
            value: order ? accountOrderStatusLabel(order.status) : "",
            control: canChangeOrderStatus() && (order?.status === "Draft" || order?.status === "Confirmed") ? (
              <AccountSelect<AccountOrder["status"]>
                className="account-document-header__control"
                aria-label={t("وضعیت")}
                aria-busy={pending === "status"}
                value={order?.status}
                disabled={!order || loading || Boolean(pending) || saveAction.pending || Boolean(attachmentTask) || dirty}
                suffixIcon={pending === "status" ? <PendingRing /> : undefined}
                options={[
                  { value: "Draft", label: t("پیش نویس") },
                  { value: "Confirmed", label: t("قطعی") },
                ]}
                onChange={function (status) { void changeStatus(status); }}
              />
            ) : undefined,
          },
          {
            key: "code",
            label: t("کد"),
            value: compactOrderNumber(order?.orderNumber),
            direction: "ltr",
          },
          {
            key: "date",
            label: t("تاریخ"),
            value: order?.orderedAt ? new Date(order.orderedAt).toLocaleDateString(getLocale()) : "",
          },
        ]}
      >
        <StoreTextField
          inputRef={titleInputRef}
          label={t("عنوان سفارش")}
          required
          value={draft.title || ""}
          disabled={disabled}
          maxLength={200}
          onChange={function (title) { change({ ...draft, title }); }}
        />
        {isCompanySalesAgent() && (isNew || order?.status === "Draft") ? (
          <OrderAgencyField
            value={draft.salesAgencyId}
            disabled={disabled || saveAction.pending}
            onChange={function (salesAgencyId) { change({ ...draft, salesAgencyId }); }}
          />
        ) : order && isCompanySalesAgent() ? (
          <div className="account-document-header__summary">
            <span>{t("نام نماینده")}</span>
            <span>{order.agencyTitle}</span>
          </div>
        ) : null}
      </AccountDocumentHeader>
      {!validId ? <p>{t("شناسه سفارش معتبر نیست.")}</p> : null}
      {!isNew && !order && !loading ? <p>{t("اطلاعات سفارش دریافت نشد.")}</p> : null}
      {uncertain ? <p className="account-order-muted">{t("نتیجه ثبت مشخص نیست؛ برای بررسی و تکمیل همان درخواست، دوباره ثبت سفارش را بزنید.")}</p> : null}
      <AccountOrderGrid
        id="account-customer-order-items-v1"
        title={isNew ? t("ایجاد سفارش") : t("ویرایش سفارش")}
        rows={draft.items}
        columns={columns}
        mobileRowActions
        loading={loading}
        onRefresh={isNew ? undefined : function () { void refresh(); }}
        backAction={(
            <Button
              aria-label={t("بازگشت به سفارش‌ها")}
              title={t("بازگشت به سفارش‌ها")}
              icon={(
                <DirectionalIcon
                  direction="back"
                  size={18}
                />
              )}
              disabled={loading || Boolean(pending) || saveAction.pending || Boolean(attachmentTask)}
              onClick={function () { navigate("/account/orders"); }}
            >
              {t("بازگشت به سفارش‌ها")}
            </Button>
        )}
        actions={(
          <>
            {editable ? (
              <>
                <Button
                  type="primary"
                  icon={<Plus size={18} />}
                  disabled={disabled || draft.items.length >= 500}
                  onClick={function () { setSelected(undefined); setSearch(""); setProducts([]); setSearching(true); setSearchFailed(false); setQuantity(1); setAddOpen(true); }}
                >
                  {t("افزودن آیتم")}
                </Button>
                {isNew || saveFailed ? (
                  <Button
                    type="primary"
                    icon={<FloppyDisk size={18} />}
                    loading={pending === "save"}
                    disabled={Boolean(pending) || loading || (!isNew && !dirty)}
                    onClick={function () { void save(); }}
                  >
                    {isNew ? t("ثبت سفارش") : t("تلاش مجدد برای ذخیره")}
                  </Button>
                ) : null}
              </>
            ) : null}
            {canChangeOrderStatus() && canWrite && order && ["Confirmed", "Received", "Approved"].includes(order.status) ? (
              <AccountButton
                danger
                disabled={loading || Boolean(pending) || saveAction.pending || Boolean(attachmentTask) || dirty}
                onClick={function () { setCancellationReason(""); setCancelOpen(true); }}
              >
                {t("لغو سفارش")}
              </AccountButton>
            ) : null}
            {!isNew && canWrite && order?.status === "Draft" ? (
              <Button
                className="account-order-delete-action"
                aria-label={t("حذف سفارش")}
                title={t("حذف سفارش")}
                danger
                icon={<Trash size={18} />}
                disabled={deleteDisabled}
                loading={pending === "delete"}
                onClick={function () { void remove(); }}
              >
                {t("حذف سفارش")}
              </Button>
            ) : null}
          </>
        )}
      />
      {order?.cancellationReason ? (
        <p>{t("دلیل لغو")}: {order.cancellationReason}</p>
      ) : null}
      <PortalDialog
        open={cancelOpen}
        title={t("لغو سفارش")}
        busy={pending === "status"}
        destructive
        onClose={function () { if (!pending) setCancelOpen(false); }}
        footer={(
          <div className="account-ui__actions">
            <AccountButton
              type="primary"
              danger
              loading={pending === "status"}
              onClick={function () { return changeStatus("Cancelled", cancellationReason); }}
            >
              {t("لغو سفارش")}
            </AccountButton>
            <AccountButton
              disabled={Boolean(pending)}
              onClick={function () { setCancelOpen(false); }}
            >
              {t("انصراف")}
            </AccountButton>
          </div>
        )}
      >
        <StoreTextField
          label={t("دلیل لغو")}
          value={cancellationReason}
          onChange={setCancellationReason}
          required
          maxLength={2000}
          disabled={Boolean(pending)}
          autoFocus
        />
      </PortalDialog>
      <AccountOrderNotes
        description={draft.description}
        disabled={disabled}
        isEditMode={!isNew}
        onChange={function (description) { change({ ...draft, description }); }}
      />
      <OrderAttachments
        orderId={order?.id}
        attachments={order?.attachments || []}
        editable={editable}
        disabled={disabled || saveFailed}
        titleReady={Boolean(draft.title?.trim())}
        task={attachmentTask}
        onUpload={uploadAttachment}
        onRemove={removeAttachment}
      />
      <AccountItemPickerDialog
        open={addOpen}
        title={t("افزودن آیتم سفارش")}
        itemLabel={t("نقشه")}
        placeholder={t("جست‌وجو و انتخاب نقشه")}
        options={productOptions}
        selected={selected ? { value: selected.productVariantId, label: selected.title } : undefined}
        quantity={quantity}
        maxQuantity={100000}
        loading={searching}
        failed={searchFailed}
        disabled={disabled}
        onSearch={function (value) {
          const next = value.trim();
          if (next === search) return;
          setSearch(next);
          setSelected(undefined);
          setProducts([]);
          setSearching(true);
          setSearchFailed(false);
        }}
        onSelect={function (value) {
          setSelected(products.find(function (product) { return product.productVariantId === value; }));
        }}
        onQuantity={setQuantity}
        onAdd={addLine}
        onClose={function () { setAddOpen(false); }}
        onRetry={function () {
          setSearching(true);
          setSearchRevision(function (value) { return value + 1; });
        }}
      />
    </section>
  );
}
